import { useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  FolderOpen,
  Megaphone,
  MessageCircle,
  Bell,
  CreditCard,
  Settings,
  Activity,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { adminService } from '@services/adminService';

type NavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { path: '/admin', label: 'Genel Bakış', icon: LayoutDashboard, end: true },
  { path: '/admin/users', label: 'Kullanıcılar', icon: Users },
  { path: '/admin/categories', label: 'Kategoriler', icon: FolderOpen },
  { path: '/admin/ads', label: 'Reklamlar', icon: Megaphone },
  { path: '/admin/whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { path: '/admin/reminders', label: 'Hatırlatıcılar', icon: Bell },
  { path: '/admin/billing', label: 'Abonelikler', icon: CreditCard },
  { path: '/admin/settings', label: 'Ayarlar', icon: Settings },
  { path: '/admin/system', label: 'Sistem', icon: Activity },
];

const TAB_REDIRECT: Record<string, string> = {
  users: '/admin/users',
  categories: '/admin/categories',
  settings: '/admin/settings',
  whatsapp: '/admin/whatsapp',
  status: '/admin/system',
  ads: '/admin/ads',
  reminders: '/admin/reminders',
  billing: '/admin/billing',
  orders: '/admin',
  overview: '/admin',
};

const MOBILE_NAV: NavItem[] = [
  { path: '/admin', label: 'Özet', icon: LayoutDashboard, end: true },
  { path: '/admin/users', label: 'Kullanıcı', icon: Users },
  { path: '/admin/categories', label: 'Kategori', icon: FolderOpen },
  { path: '/admin/ads', label: 'Reklam', icon: Megaphone },
];

function isActive(path: string, current: string, end?: boolean) {
  if (end) return current === path || current === `${path}/`;
  return current.startsWith(path);
}

export function AdminShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabParam = new URLSearchParams(location.search).get('tab');
  if (tabParam && TAB_REDIRECT[tabParam]) {
    return <Navigate to={TAB_REDIRECT[tabParam]} replace />;
  }

  const { data: systemStatus } = useQuery({
    queryKey: ['admin', 'system-status'],
    queryFn: () => adminService.getSystemStatus(),
    refetchInterval: 60000,
  });

  const systemOk =
    systemStatus?.database === 'ok' &&
    systemStatus?.redis === 'ok' &&
    systemStatus?.meilisearch === 'ok';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavLink = ({ item, onClick }: { item: NavItem; onClick?: () => void }) => {
    const active = isActive(item.path, location.pathname, item.end);
    const Icon = item.icon;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={clsx(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          active
            ? 'bg-slate-900 text-white'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        {item.label}
      </Link>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="border-b border-slate-200 px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white">
              LY
            </span>
            <div>
              <p className="text-sm font-bold text-slate-900">Yönetici Paneli</p>
              <p className="text-xs text-slate-500">Lezzet Yolda Toptan</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-xl">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Menü"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <p className="text-sm font-semibold text-slate-900">{user?.name ?? 'Yönetici'}</p>
                <p className="text-xs text-slate-500">@{user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'hidden rounded-full px-2.5 py-1 text-xs font-medium sm:inline',
                  systemOk ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
                )}
              >
                {systemOk ? 'Sistem sağlıklı' : 'Sistem uyarısı'}
              </span>
              <button
                type="button"
                onClick={() => void handleLogout()}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Çıkış</span>
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 pb-28 lg:pb-6">
          <Outlet />
        </main>

        <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur lg:hidden">
          <div className="flex justify-around py-2">
            {MOBILE_NAV.map((item) => {
              const active = isActive(item.path, location.pathname, item.end);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={clsx(
                    'flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium',
                    active ? 'text-slate-900' : 'text-slate-500',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium text-slate-500"
            >
              <Menu className="h-5 w-5" />
              Daha
            </button>
          </div>
        </nav>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Kapat"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-semibold text-slate-900">Admin Menü</p>
              <button type="button" onClick={() => setMobileMenuOpen(false)} className="rounded-lg p-2 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="space-y-1">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} />
              ))}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
